import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Room, RoomEvent, Track } from 'livekit-client';
import { api, PublicUser } from './api';
import { playRingback } from './prefs';
import { useChannelMessages } from './realtime';
import { Icon } from './icons';

export type AvPrefs = {
  mic_id?: string;
  camera_id?: string;
  speaker_id?: string;
  join_muted?: boolean;
  join_camera_off?: boolean;
  noise_suppression?: boolean;
  blur_background?: boolean;
  bg_image?: string;
  show_preview?: boolean;
  bg_mute_shortcut?: boolean;
  alone_music?: boolean;
  alone_music_after?: string;
  leave_on_lock?: boolean;
};

// A couple of built-in virtual backgrounds (SVG data URIs — no external assets needed).
const BG_PRESETS: { key: string; label: string; url: string }[] = [
  { key: 'grad-amber', label: 'Amber', url: `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#3a2a00"/><stop offset="1" stop-color="#c88a12"/></linearGradient></defs><rect width="1280" height="720" fill="url(#g)"/></svg>')}` },
  { key: 'grad-ocean', label: 'Ocean', url: `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#0b2740"/><stop offset="1" stop-color="#2f7fb8"/></linearGradient></defs><rect width="1280" height="720" fill="url(#g)"/></svg>')}` },
  { key: 'slate', label: 'Slate', url: `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720"><rect width="1280" height="720" fill="#20242e"/></svg>')}` },
];

const EMOTES = ['👍', '❤️', '😂', '🎉', '👏', '🔥', '😮', '🙏'];
const enc = new TextEncoder();
const dec = new TextDecoder();

type DataMsg =
  | { t: 'hand'; raised: boolean; name: string }
  | { t: 'emote'; emoji: string; name: string }
  | { t: 'notes'; on: boolean; name: string }
  | { t: 'rec'; on: boolean; name: string };

/** Immersive call surface: video grid, screen share, raise hand, reactions, in-call chat. */
export function CallModal({
  conversationId,
  title,
  av,
  initialCamera,
  initiated,
  meId,
  members,
  onClose,
}: {
  conversationId: string;
  title: string;
  av?: AvPrefs;
  initialCamera?: boolean;
  initiated?: boolean;
  meId?: string;
  members?: PublicUser[];
  onClose: () => void;
}) {
  const wantCamera = initialCamera !== false && !av?.join_camera_off;
  // Device choices made in-call persist on this device and win over the account
  // prefs — the last thing you picked is what the next call starts with.
  const devPref = (k: string): string => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };
  const initMic = devPref('fxmchat_call_mic') || av?.mic_id || '';
  const initCam = devPref('fxmchat_call_cam') || av?.camera_id || '';
  const initSpk = devPref('fxmchat_call_spk') || av?.speaker_id || '';
  const [room] = useState(
    () =>
      new Room({
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: {
          ...(initMic ? { deviceId: initMic } : {}),
          noiseSuppression: av?.noise_suppression !== false,
          echoCancellation: true,
          autoGainControl: true,
        },
        videoCaptureDefaults: initCam ? { deviceId: initCam } : undefined,
      }),
  );
  const [stage, setStage] = useState<'preview' | 'live'>(av?.show_preview && wantCamera ? 'preview' : 'live');
  const [identities, setIdentities] = useState<string[]>(['local']);
  const [nonce, setNonce] = useState(0);
  const [micOn, setMicOn] = useState(!av?.join_muted);
  const [camOn, setCamOn] = useState(wantCamera);
  const [sharing, setSharing] = useState(false);
  const [handRaised, setHandRaised] = useState(false);
  const [hands, setHands] = useState<Record<string, boolean>>({});
  // Minimize: shrink the call to a corner PiP (nothing unmounts, so audio + the room
  // keep running) so the rest of the app stays usable during a call.
  const [minimized, setMinimized] = useState(false);
  const [pipPos, setPipPos] = useState<{ x: number; y: number } | null>(null);
  const [joinAttempt, setJoinAttempt] = useState(0);
  const pipRef = useRef<HTMLDivElement>(null);
  const [emotes, setEmotes] = useState<{ id: number; emoji: string; name: string }[]>([]);
  const [emotePickOpen, setEmotePickOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [status, setStatus] = useState('Connecting…');
  const [err, setErr] = useState('');
  const [recordingNotes, setRecordingNotes] = useState(false);
  const [notesBy, setNotesBy] = useState<string | null>(null); // who's recording notes (for the banner)
  const notesRec = useRef<MediaRecorder | null>(null);
  const notesStart = useRef(0);
  // Plain call recording (audio posted to the channel) — independent of transcription.
  const [recordingCall, setRecordingCall] = useState(false);
  const [recBy, setRecBy] = useState<string | null>(null);
  const callRec = useRef<MediaRecorder | null>(null);
  const callRecStart = useRef(0);
  const micOnRef = useRef(micOn);
  micOnRef.current = micOn;
  const emoteId = useRef(0);
  const localName = useRef('You');
  const handRef = useRef(false);
  handRef.current = handRaised;

  // ── A/V settings (device selection, background, noise) — adjustable live in-call ──
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [rungIds, setRungIds] = useState<string[]>([]);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selMic, setSelMic] = useState(initMic);
  const [selCam, setSelCam] = useState(initCam);
  const [selSpk, setSelSpk] = useState(initSpk);
  const [noise, setNoise] = useState(av?.noise_suppression !== false);
  // Background choice persists across calls on this device (localStorage): the
  // mode key holds 'none' | 'blur' | a preset URL | 'custom', and the image key
  // holds the uploaded picture as a data URL (object URLs die with the session,
  // which is why uploads used to vanish between calls).
  const BG_MODE_KEY = 'fxmchat_call_bg';
  const BG_IMG_KEY = 'fxmchat_call_bg_img';
  const [customBg, setCustomBg] = useState<string | null>(() => {
    try { return localStorage.getItem(BG_IMG_KEY); } catch { return null; }
  });
  const [bg, setBg] = useState<string>(() => {
    try {
      const mode = localStorage.getItem(BG_MODE_KEY);
      if (mode === 'custom') return localStorage.getItem(BG_IMG_KEY) || 'none';
      if (mode) return mode;
    } catch { /* storage unavailable */ }
    return av?.bg_image || (av?.blur_background ? 'blur' : 'none');
  });
  const bgRef = useRef(bg);
  bgRef.current = bg;
  const loadDevices = async () => {
    try { setDevices(await navigator.mediaDevices.enumerateDevices()); } catch { /* permission not granted yet */ }
  };
  const camTrack = (): any => (room.localParticipant.getTrackPublication(Track.Source.Camera) as any)?.videoTrack;
  const micTrack = (): any => (room.localParticipant.getTrackPublication(Track.Source.Microphone) as any)?.audioTrack;
  const applyBg = async (mode: string) => {
    const track = camTrack();
    if (!track) return;
    try {
      if (mode === 'none') {
        if (track.getProcessor?.()) await track.stopProcessor();
        return;
      }
      const tp: any = await import('@livekit/track-processors');
      // Wasm + segmentation model served from our own origin — the CDN fetches
      // (jsdelivr/googleapis) are blocked on some corporate networks, which made
      // the effect silently do nothing. Landscape selfie model: trained on 16:9
      // webcam framing — noticeably cleaner person edges than the default square
      // model. GPU delegate with CPU fallback; radius 25 dissolves mask errors.
      const assets = {
        assetPaths: {
          tasksVisionFileSet: '/public/mediapipe/wasm',
          modelAssetPath: '/public/mediapipe/selfie_segmenter_landscape.tflite',
        },
      };
      const mkProc = (delegate: 'GPU' | 'CPU') =>
        mode === 'blur'
          ? tp.BackgroundBlur(25, { delegate }, undefined, assets)
          : tp.VirtualBackground(mode, { delegate }, undefined, assets);
      try {
        await track.setProcessor(mkProc('GPU'));
        console.info('[helm-call] background effect active (GPU)');
      } catch {
        await track.setProcessor(mkProc('CPU'));  // no WebGPU/WebGL — still works, just softer
        console.info('[helm-call] background effect active (CPU)');
      }
    } catch (e) {
      // Segmenter unavailable — usually WebGL2 missing (hardware acceleration
      // off). Revert the selection so the UI doesn't claim an effect that isn't
      // running.
      console.warn('[helm-call] background effect failed — showing raw camera', e);
      setBg('none');
      bgRef.current = 'none';
      try { if (track.getProcessor?.()) await track.stopProcessor(); } catch { /* already raw */ }
    }
  };

  // Noise cancellation, best available for the deployment:
  //   1. RNNoise (open-source neural model, WASM, runs client-side) — works with
  //      ANY LiveKit server including self-hosted. This is the primary path.
  //   2. Krisp — only ever works on LiveKit Cloud; tried opportunistically so a
  //      future move to Cloud upgrades automatically.
  //   3. Browser noiseSuppression constraint — last resort.
  const noiseProcRef = useRef<any>(null);
  const applyNoise = async (enabled: boolean) => {
    const t = micTrack();
    if (!t) return;
    if (!enabled) {
      if (noiseProcRef.current) {
        try { await t.stopProcessor(); } catch { /* already stopped */ }
        noiseProcRef.current = null;
      }
      try { if (t.restartTrack) await t.restartTrack({ deviceId: selMic || undefined, noiseSuppression: false }); } catch { /* ignore */ }
      return;
    }
    if (noiseProcRef.current) return;  // already filtering
    try {
      const { RnnoiseProcessor } = await import('./noise');
      const proc = RnnoiseProcessor();
      await t.setProcessor(proc);
      noiseProcRef.current = proc;
      return;
    } catch { /* wasm/worklet failed to load — try Krisp, then browser filter */ }
    try {
      const k: any = await import('@livekit/krisp-noise-filter');
      if (k.isKrispNoiseFilterSupported?.() !== false) {
        const filter = k.KrispNoiseFilter();
        await t.setProcessor(filter);
        noiseProcRef.current = filter;
        return;
      }
    } catch { /* not on LiveKit Cloud */ }
    try { if (t.restartTrack) await t.restartTrack({ deviceId: selMic || undefined, noiseSuppression: true, echoCancellation: true, autoGainControl: true }); console.info('[helm-call] noise suppression: browser filter only'); } catch { /* ignore */ }
  };
  const persistBg = (mode: string) => {
    try {
      if (mode.startsWith('data:')) {
        localStorage.setItem(BG_MODE_KEY, 'custom');
        localStorage.setItem(BG_IMG_KEY, mode);
      } else {
        localStorage.setItem(BG_MODE_KEY, mode);  // 'none' | 'blur' | preset URL
      }
    } catch { /* quota/unavailable — the effect still applies for this call */ }
  };
  const chooseBg = async (mode: string) => { setBg(mode); bgRef.current = mode; persistBg(mode); await applyBg(mode); };
  // Uploaded backgrounds are downscaled onto a canvas and kept as a JPEG data
  // URL — small enough for localStorage, alive across sessions.
  const uploadBg = (f: File) => {
    const img = new Image();
    const url = URL.createObjectURL(f);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, 1600 / img.width);
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height);
      const dataUrl = c.toDataURL('image/jpeg', 0.85);
      setCustomBg(dataUrl);
      chooseBg(dataUrl);
    };
    img.onerror = () => { URL.revokeObjectURL(url); };
    img.src = url;
  };
  const switchDevice = async (kind: 'audioinput' | 'videoinput' | 'audiooutput', id: string) => {
    try {
      await room.switchActiveDevice(kind, id);
      const keep = (k: string) => { try { localStorage.setItem(k, id); } catch { /* quota */ } };
      if (kind === 'audioinput') { setSelMic(id); keep('fxmchat_call_mic'); noiseProcRef.current = null; if (noise) await applyNoise(true); }
      else if (kind === 'audiooutput') { setSelSpk(id); keep('fxmchat_call_spk'); }
      else { setSelCam(id); keep('fxmchat_call_cam'); setNonce((n) => n + 1); await applyBg(bgRef.current); } // new cam track loses the processor
    } catch { /* device unavailable */ }
  };
  const toggleNoise = async () => {
    const next = !noise;
    setNoise(next);
    await applyNoise(next);
  };

  const send = (m: DataMsg) => {
    try { room.localParticipant.publishData(enc.encode(JSON.stringify(m)), { reliable: true }); } catch { /* not connected */ }
  };
  const pushEmote = (emoji: string, name: string) => {
    const id = (emoteId.current += 1);
    setEmotes((cur) => [...cur, { id, emoji, name }]);
    window.setTimeout(() => setEmotes((cur) => cur.filter((e) => e.id !== id)), 3200);
  };

  // ── Shared audio capture: mix everyone's tracks into one MediaRecorder. Used by
  // both Transcribe (server transcribe+summarize) and Record (audio posted to chat).
  const mixAndRecord = (onDone: (blob: Blob) => void): MediaRecorder => {
    const ac = new AudioContext();
    const dest = ac.createMediaStreamDestination();
    const addTrack = (t?: MediaStreamTrack | null) => {
      if (t) { try { ac.createMediaStreamSource(new MediaStream([t])).connect(dest); } catch { /* skip */ } }
    };
    addTrack(room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track?.mediaStreamTrack);
    room.remoteParticipants.forEach((p) => {
      p.trackPublications.forEach((pub: any) => {
        if (pub.kind === Track.Kind.Audio && pub.track) addTrack(pub.track.mediaStreamTrack);
      });
    });
    const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
    const rec = new MediaRecorder(dest.stream, { mimeType: mime });
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => {
      ac.close().catch(() => {});
      const blob = new Blob(chunks, { type: 'audio/webm' });
      if (blob.size >= 2000) onDone(blob); // ignore empty captures
    };
    rec.start();
    return rec;
  };

  // Transcribe → meeting notes (transcript + summary posted by the server).
  const startNotes = () => {
    try {
      notesRec.current = mixAndRecord(async (blob) => {
        const durationMs = notesStart.current ? Date.now() - notesStart.current : undefined;
        try {
          const file = new File([blob], `meeting-${Date.now()}.webm`, { type: 'audio/webm' });
          const att = await api.uploadFile(file);
          const participants = identities.map((id) => (id === 'local' ? localName.current : tileName(room, id)));
          await api.meetingNotes(conversationId, {
            attachment: { ...att, duration_ms: durationMs }, participants, duration_ms: durationMs,
          });
        } catch { /* best-effort — notes just won't post */ }
      });
      notesStart.current = Date.now();
      setRecordingNotes(true);
      setNotesBy(localName.current);
      send({ t: 'notes', on: true, name: localName.current });
    } catch {
      window.alert('Could not start transcription.');
    }
  };
  const stopNotes = () => {
    setRecordingNotes(false);
    setNotesBy(null);
    try { notesRec.current?.stop(); } catch { /* ignore */ }
    notesRec.current = null;
    send({ t: 'notes', on: false, name: localName.current });
  };
  const toggleNotes = () => { if (recordingNotes) stopNotes(); else startNotes(); };

  // Record → the raw call audio lands in the channel as a playable attachment.
  const startRec = () => {
    try {
      callRec.current = mixAndRecord(async (blob) => {
        const durationMs = callRecStart.current ? Date.now() - callRecStart.current : undefined;
        try {
          const file = new File([blob], `call-recording-${Date.now()}.webm`, { type: 'audio/webm' });
          const att = await api.uploadFile(file);
          const mins = durationMs ? Math.max(1, Math.round(durationMs / 60000)) : 0;
          await api.sendMessageWith(conversationId, `📼 Call recording${mins ? ` (${mins} min)` : ''}`, [{ ...att, duration_ms: durationMs } as any]);
        } catch { /* best-effort — recording just won't post */ }
      });
      callRecStart.current = Date.now();
      setRecordingCall(true);
      setRecBy(localName.current);
      send({ t: 'rec', on: true, name: localName.current });
    } catch {
      window.alert('Could not start recording.');
    }
  };
  const stopRec = () => {
    setRecordingCall(false);
    setRecBy(null);
    try { callRec.current?.stop(); } catch { /* ignore */ }
    callRec.current = null;
    send({ t: 'rec', on: false, name: localName.current });
  };
  const toggleRec = () => { if (recordingCall) stopRec(); else startRec(); };
  // If the call ends mid-capture, finalize both (onstop still uploads + posts).
  useEffect(() => () => {
    try { notesRec.current?.stop(); } catch { /* ignore */ }
    try { callRec.current?.stop(); } catch { /* ignore */ }
  }, []);

  // ── Ringback: the CALLER hears the waiting tone until someone picks up ──
  // Only when we initiated the call and are still alone; stops the moment a
  // second participant appears, on error, or after ~60s of no answer.
  const alone = identities.length <= 1;
  useEffect(() => {
    if (stage !== 'live' || !initiated || !alone || !!err) return undefined;
    playRingback();
    let cycles = 0;
    const iv = window.setInterval(() => {
      if (++cycles >= 20) { window.clearInterval(iv); return; }
      playRingback();
    }, 3000);
    return () => window.clearInterval(iv);
  }, [stage, initiated, alone, err]);

  // ── Preview stage: camera/mic check before joining ──
  const previewRef = useRef<HTMLVideoElement>(null);
  const previewStream = useRef<MediaStream | null>(null);
  useEffect(() => {
    if (stage !== 'preview') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          audio: av?.mic_id ? { deviceId: av.mic_id } : true,
          video: av?.camera_id ? { deviceId: av.camera_id } : true,
        });
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        previewStream.current = s;
        if (previewRef.current) previewRef.current.srcObject = s;
      } catch { /* best-effort */ }
    })();
    return () => {
      cancelled = true;
      previewStream.current?.getTracks().forEach((t) => t.stop());
      previewStream.current = null;
    };
  }, [stage, av?.mic_id, av?.camera_id]);

  // ── Live stage: connect + wire events ──
  useEffect(() => {
    if (stage !== 'live') return undefined;
    let cancelled = false;
    const refresh = () => {
      setIdentities(['local', ...Array.from(room.remoteParticipants.values()).map((pp) => pp.identity)]);
      setNonce((n) => n + 1);
    };
    (async () => {
      try {
        const { url, token } = await api.callToken(conversationId);
        room
          .on(RoomEvent.ParticipantConnected, () => { refresh(); if (handRef.current) send({ t: 'hand', raised: true, name: localName.current }); })
          .on(RoomEvent.ParticipantDisconnected, refresh)
          .on(RoomEvent.TrackSubscribed, refresh)
          .on(RoomEvent.TrackUnsubscribed, refresh)
          .on(RoomEvent.LocalTrackPublished, refresh)
          .on(RoomEvent.LocalTrackUnpublished, refresh)
          .on(RoomEvent.TrackMuted, refresh)
          .on(RoomEvent.TrackUnmuted, refresh)
          .on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: any) => {
            try {
              const d = JSON.parse(dec.decode(payload)) as DataMsg;
              const who = participant?.identity || 'someone';
              if (d.t === 'hand') setHands((h) => ({ ...h, [who]: d.raised }));
              else if (d.t === 'emote') pushEmote(d.emoji, d.name || 'Someone');
              else if (d.t === 'notes') setNotesBy(d.on ? (d.name || 'Someone') : null);
              else if (d.t === 'rec') setRecBy(d.on ? (d.name || 'Someone') : null);
            } catch { /* ignore malformed */ }
          })
          .on(RoomEvent.LocalTrackUnpublished, (pub: any) => {
            // Browser-native "Stop sharing" ends the capture track and LiveKit
            // unpublishes it — without this the app still thought it was sharing.
            if (pub?.source === Track.Source.ScreenShare) { setSharing(false); setNonce((n) => n + 1); }
          })
          .on(RoomEvent.Disconnected, () => { if (!cancelled) onClose(); });
        await room.connect(url, token);
        localName.current = room.localParticipant.name || 'You';
        await room.localParticipant.setMicrophoneEnabled(micOnRef.current);
        await room.localParticipant.setCameraEnabled(camOn);
        if (cancelled) { room.disconnect(); return; }
        if (camOn && bgRef.current !== 'none') { applyBg(bgRef.current); }  // blur/virtual background from prefs
        if (micOnRef.current && noise) { applyNoise(true); }  // Krisp if available, else browser filter
        if (selSpk) { room.switchActiveDevice('audiooutput', selSpk).catch(() => {}); }
        setStatus('');
        refresh();
      } catch (e: any) {
        if (cancelled) return;
        // Transient network blips ("Failed to fetch" — e.g. an API restart mid-
        // deploy) resolve themselves: retry once automatically before surfacing.
        if (joinAttempt === 0 && /fetch|network/i.test(String(e?.message || ''))) {
          window.setTimeout(() => { if (!cancelled) setJoinAttempt(1); }, 800);
          return;
        }
        setErr(e?.message || 'Could not join the call');
      }
    })();
    return () => { cancelled = true; room.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, joinAttempt]);

  const toggleMic = async () => {
    const next = !micOnRef.current;
    setMicOn(next);
    if (stage === 'live') {
      await room.localParticipant.setMicrophoneEnabled(next);
      // A first-time unmute publishes a fresh track — the noise processor must
      // attach to it or suppression silently never engages.
      if (next && noise && !noiseProcRef.current) await applyNoise(true);
    }
  };
  const toggleCam = async () => {
    const next = !camOn;
    setCamOn(next);
    if (stage === 'live') {
      await room.localParticipant.setCameraEnabled(next);
      if (next && bgRef.current !== 'none') await applyBg(bgRef.current);  // re-apply background to the fresh track
      setNonce((n) => n + 1);
    }
  };
  const toggleShare = async () => {
    try {
      const next = !sharing;
      await room.localParticipant.setScreenShareEnabled(next);
      setSharing(next);
      setNonce((n) => n + 1);
    } catch { /* user cancelled the picker */ }
  };
  const toggleHand = () => {
    const next = !handRaised;
    setHandRaised(next);
    setHands((h) => ({ ...h, local: next }));
    send({ t: 'hand', raised: next, name: localName.current });
  };
  const emote = (emoji: string) => {
    setEmotePickOpen(false);
    pushEmote(emoji, localName.current);
    send({ t: 'emote', emoji, name: localName.current });
  };
  const leave = () => { room.disconnect(); onClose(); };

  // Ctrl+Shift+Space mute shortcut
  useEffect(() => {
    if (stage !== 'live' || av?.bg_mute_shortcut === false) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.code === 'Space' || e.key === ' ')) { e.preventDefault(); toggleMic(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, av?.bg_mute_shortcut]);

  // ── Preview UI ──
  if (stage === 'preview') {
    return createPortal(
      <div className="fx-callroom is-preview">
        <div className="fx-callroom-head"><span className="fx-callroom-title">📞 {title}</span>
          <span className="fx-callroom-status">Check your camera and mic, then join</span></div>
        <div className="fx-callroom-stage">
          <div className="fx-callroom-grid one">
            <div className="fx-vtile"><video ref={previewRef} autoPlay playsInline muted />
              <span className="fx-vtile-label">Preview{micOn ? '' : ' · muted'}</span></div>
          </div>
        </div>
        <div className="fx-callroom-controls">
          <CtrlBtn on={micOn} onClick={toggleMic} icon={micOn ? 'mic' : 'micoff'} label={micOn ? 'Mute' : 'Unmute'} />
          <CtrlBtn on={camOn} onClick={toggleCam} icon="video" label={camOn ? 'Stop video' : 'Start video'} />
          <button type="button" className="fx-ctrl-join" onClick={() => { previewStream.current?.getTracks().forEach((t) => t.stop()); previewStream.current = null; setStage('live'); }}>Join now</button>
          <CtrlBtn danger icon="close" label="Cancel" onClick={onClose} />
        </div>
      </div>,
      document.body,
    );
  }

  const sharer = identities.find((id) => hasVideo(room, id, 'screen'));

  // Leave the call when the screen locks (av.leave_on_lock) — IdleDetector,
  // permission requested from Preferences; silently inactive if unsupported/denied.
  useEffect(() => {
    if (!av?.leave_on_lock || !(window as any).IdleDetector) return undefined;
    const ctrl = new AbortController();
    (async () => {
      try {
        const det = new (window as any).IdleDetector();
        det.addEventListener('change', () => { if (det.screenState === 'locked') leave(); });
        await det.start({ threshold: 60000, signal: ctrl.signal });
      } catch { /* unsupported or permission not granted */ }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Minimized PiP is draggable by its header; a plain tap (no movement) expands.
  const startPipDrag = (e: React.PointerEvent) => {
    if (!minimized) return;
    if ((e.target as Element).closest('button')) return;  // buttons keep their clicks
    const el = pipRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const sx = e.clientX;
    const sy = e.clientY;
    let moved = false;
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - sx;
      const dy = ev.clientY - sy;
      if (!moved && Math.hypot(dx, dy) > 5) moved = true;
      if (moved) {
        setPipPos({
          x: Math.min(Math.max(8, rect.left + dx), window.innerWidth - rect.width - 8),
          y: Math.min(Math.max(8, rect.top + dy), window.innerHeight - rect.height - 8),
        });
      }
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (!moved) setMinimized(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    e.preventDefault();
  };

  return createPortal(
    <div
      ref={pipRef}
      className={`fx-callroom${chatOpen ? ' is-chat' : ''}${minimized ? ' is-min' : ''}`}
      style={minimized && pipPos ? { left: pipPos.x, top: pipPos.y, right: 'auto', bottom: 'auto' } : undefined}
    >
      <div className="fx-callroom-head" onPointerDown={startPipDrag}>
        <span className="fx-callroom-title">📞 {title}</span>
        {status && <span className="fx-callroom-status">{status}</span>}
        {!status && identities.length === 1 && <span className="fx-callroom-status">Waiting for others…</span>}
        <span className="fx-callroom-count">{identities.length} in call</span>
        <button type="button" className="fx-callroom-min" title={minimized ? 'Expand' : 'Minimize (keep the call and use the app)'}
          aria-label={minimized ? 'Expand call' : 'Minimize call'}
          onClick={(e) => { e.stopPropagation(); setMinimized((m) => !m); }}>{minimized ? '⤢' : '—'}</button>
        {notesBy && (
          <span className="fx-notes-rec" title="This call is being transcribed for meeting notes">
            <span className="fx-notes-dot" /> 📝 {recordingNotes ? 'Transcribing' : `${notesBy} is transcribing`}
          </span>
        )}
        {recBy && (
          <span className="fx-notes-rec" title="This call is being recorded — the audio will be posted to the channel">
            <span className="fx-notes-dot" /> ⏺ {recordingCall ? 'Recording' : `${recBy} is recording`}
          </span>
        )}
      </div>

      <div className="fx-callroom-body">
        <div className="fx-callroom-stage">
          {err ? (
            <div className="fx-call-err">
              {err}
              <button type="button" className="fx-sendbtn" onClick={() => { setErr(''); setStatus('Connecting…'); setJoinAttempt((a) => a + 1); }}>Try again</button>
              <button type="button" className="fx-login-back" onClick={onClose}>Close</button>
            </div>
          ) : (
            <>
              {sharer && (
                <div className="fx-callroom-screen">
                  <Tile room={room} identity={sharer} source="screen" nonce={nonce} label={tileName(room, sharer)} big />
                </div>
              )}
              <div
                className={`fx-callroom-grid${sharer ? ' strip' : identities.length <= 1 ? ' one' : ''}`}
                style={sharer || identities.length <= 1 ? undefined
                  : { gridTemplateColumns: `repeat(${Math.ceil(Math.sqrt(identities.length))}, minmax(0, 1fr))` }}
              >
                {identities.map((id) => (
                  <Tile key={id} room={room} identity={id} source="camera" nonce={nonce}
                    label={id === 'local' ? 'You' : tileName(room, id)} hand={!!hands[id]} isLocal={id === 'local'} localMicOn={micOn} />
                ))}
              </div>
            </>
          )}
          <div className="fx-emote-layer">
            {emotes.map((e) => (
              <span key={e.id} className="fx-emote-float" style={{ left: `${8 + (e.id * 37) % 78}%` }}>
                {e.emoji}<span className="fx-emote-name">{e.name}</span>
              </span>
            ))}
          </div>
        </div>

        {chatOpen && <CallChat conversationId={conversationId} meId={meId} onClose={() => setChatOpen(false)} />}
      </div>

      <div className="fx-callroom-controls">
        <CtrlBtn on={micOn} onClick={toggleMic} icon={micOn ? 'mic' : 'micoff'} label={micOn ? 'Mute' : 'Unmute'} off={!micOn} />
        <CtrlBtn on={camOn} onClick={toggleCam} icon="video" label={camOn ? 'Stop video' : 'Start video'} off={!camOn} />
        <CtrlBtn on={sharing} onClick={toggleShare} icon="screen" label={sharing ? 'Stop share' : 'Share screen'} active={sharing} />
        <div className="fx-ctrl-popwrap">
          <button type="button" className={`fx-ctrl-btn${addOpen ? ' is-active' : ''}`} title="Add people to the call — anyone in the workspace"
            onClick={() => setAddOpen((v) => !v)}>
            <Icon name="userplus" size={20} /><span className="fx-ctrl-label">Add</span>
          </button>
          {addOpen && (() => {
            const inCall = new Set(identities.map((id) => id.replace(/^user:/, '')));
            const candidates = (members || []).filter((mm) => mm.id !== meId && !inCall.has(mm.id));
            return (
              <div className="fx-call-settings" style={{ maxHeight: 320, overflowY: 'auto', minWidth: 230 }} onClick={(e) => e.stopPropagation()}>
                <div className="fx-call-set-label">Ring into this call</div>
                {candidates.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--fx-muted)', padding: '6px 2px' }}>Everyone in the workspace is already here.</div>}
                {candidates.map((mm) => (
                  <button
                    type="button"
                    key={mm.id}
                    className="fx-acct-item"
                    disabled={rungIds.includes(mm.id)}
                    onClick={async () => {
                      try { await api.ringCall(conversationId, [mm.id], true); setRungIds((r) => [...r, mm.id]); } catch { /* best-effort */ }
                    }}
                  >
                    {mm.name || mm.email}{rungIds.includes(mm.id) ? ' · ringing…' : ''}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
        <button type="button" className={`fx-ctrl-btn${recordingNotes ? ' is-active' : ''}`} onClick={toggleNotes}
          title={recordingNotes ? 'Stop transcribing' : 'Transcribe the call — meeting notes with summary post to the channel'}>
          <span className="fx-ctrl-emoji">📝</span><span className="fx-ctrl-label">{recordingNotes ? 'Stop transcript' : 'Transcribe'}</span>
        </button>
        <button type="button" className={`fx-ctrl-btn${recordingCall ? ' is-active' : ''}`} onClick={toggleRec}
          title={recordingCall ? 'Stop recording' : 'Record the call — the audio posts to the channel when you stop'}>
          <span className="fx-ctrl-emoji">⏺</span><span className="fx-ctrl-label">{recordingCall ? 'Stop recording' : 'Record'}</span>
        </button>
        <button type="button" className={`fx-ctrl-btn${handRaised ? ' is-active' : ''}`} onClick={toggleHand} title="Raise hand">
          <span className="fx-ctrl-emoji">✋</span><span className="fx-ctrl-label">{handRaised ? 'Lower' : 'Raise'}</span>
        </button>
        <div className="fx-ctrl-popwrap">
          <button type="button" className="fx-ctrl-btn" onClick={() => setEmotePickOpen((v) => !v)} title="React">
            <span className="fx-ctrl-emoji">😊</span><span className="fx-ctrl-label">React</span>
          </button>
          {emotePickOpen && (
            <div className="fx-emote-pick">
              {EMOTES.map((e) => <button type="button" key={e} onClick={() => emote(e)}>{e}</button>)}
            </div>
          )}
        </div>
        <button type="button" className={`fx-ctrl-btn${chatOpen ? ' is-active' : ''}`} onClick={() => setChatOpen((v) => !v)} title="In-call chat">
          <Icon name="dm" size={20} /><span className="fx-ctrl-label">Chat</span>
        </button>
        <div className="fx-ctrl-popwrap">
          <button type="button" className={`fx-ctrl-btn${settingsOpen ? ' is-active' : ''}`} title="More options — camera, mic, speaker & background"
            onClick={() => { setSettingsOpen((v) => !v); if (!settingsOpen) loadDevices(); }}>
            <Icon name="gear" size={20} /><span className="fx-ctrl-label">More options</span>
          </button>
          {settingsOpen && (
            <div className="fx-call-settings" onClick={(e) => e.stopPropagation()}>
              <div className="fx-call-set-label">Camera</div>
              <select className="fx-call-set-sel" value={selCam} onChange={(e) => switchDevice('videoinput', e.target.value)}>
                {devices.filter((d) => d.kind === 'videoinput').length === 0 && <option value="">Default camera</option>}
                {devices.filter((d) => d.kind === 'videoinput').map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Camera'}</option>)}
              </select>
              <div className="fx-call-set-label">Microphone</div>
              <select className="fx-call-set-sel" value={selMic} onChange={(e) => switchDevice('audioinput', e.target.value)}>
                {devices.filter((d) => d.kind === 'audioinput').length === 0 && <option value="">Default mic</option>}
                {devices.filter((d) => d.kind === 'audioinput').map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Microphone'}</option>)}
              </select>
              <div className="fx-call-set-label">Speaker</div>
              <select className="fx-call-set-sel" value={selSpk} onChange={(e) => switchDevice('audiooutput', e.target.value)}>
                {devices.filter((d) => d.kind === 'audiooutput').length === 0 && <option value="">Default speaker</option>}
                {devices.filter((d) => d.kind === 'audiooutput').map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Speaker'}</option>)}
              </select>
              <div className="fx-call-set-label">Background</div>
              <div className="fx-call-bg-row">
                <button type="button" className={`fx-call-bg${bg === 'none' ? ' on' : ''}`} onClick={() => chooseBg('none')}>None</button>
                <button type="button" className={`fx-call-bg${bg === 'blur' ? ' on' : ''}`} onClick={() => chooseBg('blur')}>Blur</button>
                {BG_PRESETS.map((pset) => (
                  <button type="button" key={pset.key} className={`fx-call-bg is-img${bg === pset.url ? ' on' : ''}`}
                    style={{ backgroundImage: `url("${pset.url}")` }} onClick={() => chooseBg(pset.url)} title={pset.label} aria-label={pset.label} />
                ))}
                {customBg && (
                  <button type="button" className={`fx-call-bg is-img${bg === customBg ? ' on' : ''}`}
                    style={{ backgroundImage: `url("${customBg}")` }} onClick={() => chooseBg(customBg)}
                    title="Your image" aria-label="Your uploaded background" />
                )}
                <label className="fx-call-bg is-upload" title="Upload a background image">
                  ＋<input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadBg(f); e.target.value = ''; }} />
                </label>
              </div>
              <label className="fx-call-set-toggle">
                <input type="checkbox" checked={noise} onChange={toggleNoise} /> Background noise reduction
              </label>
            </div>
          )}
        </div>
        <button type="button" className="fx-ctrl-leave" onClick={leave}><Icon name="phone" size={18} /> Leave</button>
      </div>
    </div>,
    document.body,
  );
}

function CtrlBtn({ icon, label, on, off, active, danger, onClick }: {
  icon: string; label: string; on?: boolean; off?: boolean; active?: boolean; danger?: boolean; onClick: () => void;
}) {
  return (
    <button type="button" className={`fx-ctrl-btn${off ? ' is-off' : ''}${active ? ' is-active' : ''}${danger ? ' is-danger' : ''}`} onClick={onClick} title={label}>
      <Icon name={icon} size={20} /><span className="fx-ctrl-label">{label}</span>
    </button>
  );
}

function hasVideo(room: Room, identity: string, source: 'camera' | 'screen'): boolean {
  const p = identity === 'local' ? room.localParticipant : room.remoteParticipants.get(identity);
  if (!p) return false;
  const src = source === 'screen' ? Track.Source.ScreenShare : Track.Source.Camera;
  // A camera that's toggled off stays PUBLISHED but muted — treat muted as "no video"
  // so the tile falls back to the name initial instead of a frozen/black frame.
  return Array.from(p.trackPublications.values() as Iterable<any>).some((pub: any) => pub.kind === Track.Kind.Video && pub.source === src && pub.track && !pub.isMuted);
}
function tileName(room: Room, identity: string): string {
  const p = room.remoteParticipants.get(identity);
  return (p?.name || identity.replace(/^user:/, '')) as string;
}

function Tile({ room, identity, source, nonce, label, hand, isLocal, localMicOn, big }: {
  room: Room; identity: string; source: 'camera' | 'screen'; nonce: number; label: string;
  hand?: boolean; isLocal?: boolean; localMicOn?: boolean; big?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const p = isLocal ? room.localParticipant : room.remoteParticipants.get(identity);
    if (!p) return undefined;
    const pubs = Array.from(p.trackPublications.values() as Iterable<any>) as any[];
    const src = source === 'screen' ? Track.Source.ScreenShare : Track.Source.Camera;
    const vpub = pubs.find((pub) => pub.kind === Track.Kind.Video && pub.source === src && pub.track);
    const apub = !isLocal && source === 'camera' ? pubs.find((pub) => pub.kind === Track.Kind.Audio && pub.track) : undefined;
    if (vpub?.track && videoRef.current) vpub.track.attach(videoRef.current);
    if (apub?.track && audioRef.current) apub.track.attach(audioRef.current);
    return () => {
      if (vpub?.track && videoRef.current) vpub.track.detach(videoRef.current);
      if (apub?.track && audioRef.current) apub.track.detach(audioRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, identity, source]);

  const p = isLocal ? room.localParticipant : room.remoteParticipants.get(identity);
  const hasVid = hasVideo(room, identity, source);
  // Mic mute: local from the toggle, remote from the participant's published mic state.
  const micMuted = source === 'screen' ? false : isLocal ? !localMicOn : !(p?.isMicrophoneEnabled ?? false);
  const initial = (label.trim()[0] || '?').toUpperCase();
  return (
    <div className={`fx-vtile${big ? ' is-big' : ''}`}>
      <video ref={videoRef} autoPlay playsInline muted={isLocal || source === 'screen'} className={hasVid ? '' : 'is-empty'} />
      {!hasVid && source === 'camera' && (
        <span className="fx-vtile-ph"><span className="fx-vtile-avatar">{initial}</span></span>
      )}
      {!isLocal && <audio ref={audioRef} autoPlay />}
      {hand && <span className="fx-vtile-hand">✋</span>}
      {micMuted && <span className="fx-vtile-mic" title="Muted">🔇</span>}
      <span className="fx-vtile-label">{label}{source === 'screen' ? ' — screen' : ''}</span>
    </div>
  );
}

/** In-call chat — reuses the conversation's live message feed (shared subscription). */
function CallChat({ conversationId, meId, onClose }: { conversationId: string; meId?: string; onClose: () => void }) {
  const { messages } = useChannelMessages(conversationId);
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length]);
  const send = async () => {
    const b = draft.trim();
    if (!b) return;
    setDraft('');
    try { await api.sendMessage(conversationId, b); } catch { setDraft(b); }
  };
  return (
    <div className="fx-callchat">
      <div className="fx-callchat-head">In-call chat<button type="button" onClick={onClose}><Icon name="close" size={15} /></button></div>
      <div className="fx-callchat-msgs">
        {messages.length === 0 && <div className="fx-callchat-empty">Messages sent here go to everyone in the conversation.</div>}
        {messages.slice(-40).map((m) => (
          <div className={`fx-callchat-m${m.sender_id === meId ? ' is-me' : ''}`} key={m.id}>
            <span className="fx-callchat-who">{m.sender_id === meId ? 'You' : m.sender_name || (m.sender_type === 'agent' ? 'Agent' : 'Member')}</span>
            <span className="fx-callchat-body">{m.body}</span>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="fx-callchat-compose">
        <input value={draft} placeholder="Message the call…" onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }} />
        <button type="button" onClick={send} disabled={!draft.trim()}><Icon name="plane" size={15} /></button>
      </div>
    </div>
  );
}
